import {
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import debounce from "lodash.debounce";
import Icon from "@mdi/react";
import { mdiFilterVariant } from "@mdi/js";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  FilterSheet,
  FilterClearButton,
  DateRangeFilter,
} from "../../../ui/new_components/common/responsive";
import { urls } from "../../../utils/urls";
import { getFaultDashboardApi } from "../service/request_desk_repository";
import { headingBar, RD, ReasonForError } from "../models/types";

interface FaultRequestRow {
  id: string;
  request_no: string;
  title: string;
  entity_type: string;
  entity_ref: string;
  entity_stage: string;
  reason_for_error: string;
  reason_detail?: string;
  role: string;
  status: string;
  created_at?: string;
}

interface UserRow {
  uid: string;
  name: string;
  as_creator: number;
  as_approver: number;
  post_inward: number;
  requests?: FaultRequestRow[];
}

const defaultFilters = () => ({
  entity_type: "",
  reason_for_error: "",
  department: "",
  from: "",
  to: "",
});

const StatCard: FunctionComponent<{ n: any; label: string; sub?: string }> = ({
  n,
  label,
  sub,
}) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="text-2xl font-bold">{n}</div>
    <div className="text-xs text-gray-500 mt-1">{label}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

const Bar: FunctionComponent<{
  label: string;
  value: number;
  max: number;
  color?: string;
}> = ({ label, value, max, color }) => (
  <div className="flex items-center gap-2 mb-2 text-xs">
    <span className="w-40 text-gray-600 truncate" title={label}>
      {label}
    </span>
    <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
      <div
        className="h-full rounded"
        style={{
          width: `${max ? (value / max) * 100 : 0}%`,
          backgroundColor: color || RD.accent,
        }}
      />
    </div>
    <span className="w-8 text-right font-semibold">{value}</span>
  </div>
);

const RequestDeskFaultDashboard: FunctionComponent = () => {
  const { showToast } = useToast();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  // Open inline on desktop, closed on mobile (the sheet is a drawer there).
  const [showFilter, setShowFilter] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768,
  );
  const [filters, setFilters] = useState<any>(defaultFilters());

  const fetchData = async (f: any) => {
    setLoading(true);
    const params: any = {};
    Object.keys(f).forEach((k) => {
      if (f[k]) params[k] = f[k];
    });
    const res = await getFaultDashboardApi(params);
    setLoading(false);
    if (res.success) setData(res.data);
    else
      showToast({ type: ToastType.error, text: res.error || "Failed to load" });
  };

  // Filters apply themselves — no Apply button. Debounced so typing in the
  // department box doesn't fire a call per keystroke.
  const debouncedFetch = useMemo(
    () => debounce((f: any) => fetchData(f), 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    debouncedFetch(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  // Below md the sheet is a drawer over the content — keep it closed there even
  // when the viewport shrinks after mount (rotate / resize).
  useEffect(() => {
    const handleResize = () => setShowFilter(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const summary = data?.summary;
  const users: UserRow[] = data?.by_user ?? [];
  const byReason = data?.by_reason ?? [];
  const trend = data?.trend ?? [];
  const maxReason = Math.max(1, ...byReason.map((r: any) => r.count));
  const maxTrend = Math.max(1, ...trend.map((t: any) => t.count));

  return (
    <div className="p-4">
      {loading && <LoadingWidget />}
      <div className="flex justify-between items-center mb-2">
        <h1
          className="text-lg font-semibold border-l-4 pl-2"
          style={headingBar}
        >
          Fault Dashboard
        </h1>
        <div className="flex gap-2 items-center">
          <h6 className="font-normal text-xs">Filters</h6>
          <div
            onClick={() => setShowFilter((v) => !v)}
            className="flex items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white cursor-pointer"
          >
            <Icon path={mdiFilterVariant} className="w-4 h-4" />
          </div>
        </div>
      </div>
      <div className="bg-purple-50 border border-purple-200 text-purple-700 rounded p-2 text-xs mb-3">
        🔒 Restricted view — counts only <b>approved</b> (confirmed) service
        requests (
        <span className="font-semibold">
          type=service_request, approval_status=approved
        </span>
        ).
      </div>

      {/* filters — same shell/grid as the procurement lists, auto-applied */}
      <div className="mb-3">
        <FilterSheet
          show={showFilter}
          onClose={() => setShowFilter(false)}
          onReset={() => setFilters(defaultFilters())}
          gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2 items-start"
        >
          <div>
            <label>Raised between</label>
            <div className="flex items-center gap-1">
              <div className="flex-1 min-w-0">
                <DateRangeFilter
                  compact
                  showChips={false}
                  fromDate={filters.from || undefined}
                  toDate={filters.to || undefined}
                  onFromChange={(v) =>
                    setFilters((o: any) => ({ ...o, from: v ?? "" }))
                  }
                  onToChange={(v) =>
                    setFilters((o: any) => ({ ...o, to: v ?? "" }))
                  }
                />
              </div>
              {(filters.from || filters.to) && (
                <FilterClearButton
                  title="Clear date range"
                  onClick={() =>
                    setFilters((o: any) => ({ ...o, from: "", to: "" }))
                  }
                />
              )}
            </div>
          </div>
          <div>
            <label>Document type</label>
            <div className="flex items-center gap-1">
              <select
                className="border rounded px-2 py-1.5 text-sm w-full"
                value={filters.entity_type}
                onChange={(e) =>
                  setFilters((o: any) => ({ ...o, entity_type: e.target.value }))
                }
              >
                <option value="">All entities</option>
                <option value="PO">PO</option>
                <option value="SO">SO</option>
                <option value="ST">ST</option>
                <option value="OTHER">Other</option>
              </select>
              {filters.entity_type && (
                <FilterClearButton
                  title="Clear document type"
                  onClick={() =>
                    setFilters((o: any) => ({ ...o, entity_type: "" }))
                  }
                />
              )}
            </div>
          </div>
          <div>
            <label>Reason for error</label>
            <div className="flex items-center gap-1">
              <select
                className="border rounded px-2 py-1.5 text-sm w-full"
                value={filters.reason_for_error}
                onChange={(e) =>
                  setFilters((o: any) => ({
                    ...o,
                    reason_for_error: e.target.value,
                  }))
                }
              >
                <option value="">All reasons</option>
                {ReasonForError.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              {filters.reason_for_error && (
                <FilterClearButton
                  title="Clear reason for error"
                  onClick={() =>
                    setFilters((o: any) => ({ ...o, reason_for_error: "" }))
                  }
                />
              )}
            </div>
          </div>
          <div>
            <label>Department</label>
            <div className="flex items-center gap-1">
              <input
                className="border rounded px-2 py-1.5 text-sm w-full"
                placeholder="Department"
                value={filters.department}
                onChange={(e) =>
                  setFilters((o: any) => ({ ...o, department: e.target.value }))
                }
              />
              {filters.department && (
                <FilterClearButton
                  title="Clear department"
                  onClick={() =>
                    setFilters((o: any) => ({ ...o, department: "" }))
                  }
                />
              )}
            </div>
          </div>
        </FilterSheet>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard
          n={summary?.confirmed_errors ?? 0}
          label="Confirmed data errors"
        />
        <StatCard
          n={summary?.on_po_post_inward ?? 0}
          label="On POs post-inward"
          sub="most expensive to fix"
        />
        <StatCard
          n={summary?.users_attributed ?? 0}
          label="Users attributed"
          sub="creators + approvers"
        />
        <StatCard
          n={`${summary?.avg_resolution_days ?? 0}d`}
          label="Avg. resolution time"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* by user */}
        <div className="bg-white rounded-lg shadow overflow-auto">
          <h3 className="font-semibold text-sm p-3">
            Errors by user{" "}
            <span className="text-gray-400 font-normal">
              — click to drill down
            </span>
          </h3>
          <table className="w-full text-xs divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {["User", "As creator", "As approver", "Post-inward"].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-3 py-2 text-left text-gray-500 uppercase"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <div className="h-24 flex items-center justify-center text-gray-400">
                      No confirmed errors
                    </div>
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <Fragment key={u.uid}>
                  <tr
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() =>
                      setExpanded(expanded === u.uid ? null : u.uid)
                    }
                  >
                    <td className="px-3 py-2 font-semibold">
                      <span
                        className="inline-block mr-1 text-gray-400"
                        style={{
                          transform:
                            expanded === u.uid
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                        }}
                      >
                        ▶
                      </span>
                      {u.name}
                    </td>
                    <td className="px-3 py-2">
                      <span className="bg-red-50 text-red-600 rounded-full px-2 py-0.5 font-semibold">
                        {u.as_creator}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="bg-amber-50 text-amber-700 rounded-full px-2 py-0.5 font-semibold">
                        {u.as_approver}
                      </span>
                    </td>
                    <td className="px-3 py-2">{u.post_inward}</td>
                  </tr>
                  {expanded === u.uid && (
                    <tr className="border-b">
                      <td colSpan={4} className="p-0">
                        <div style={{ backgroundColor: RD.surface2 }}>
                          {(u.requests ?? []).length === 0 && (
                            <div className="px-3 py-3 text-gray-400">
                              No attributed requests
                            </div>
                          )}
                          {(u.requests ?? []).map((r) => (
                            <div
                              key={`${r.id}-${r.role}`}
                              className="px-3 py-2 border-b cursor-pointer hover:bg-white"
                              onClick={() =>
                                history.push(
                                  `${urls.request_desk_detail}/${r.id}`,
                                )
                              }
                            >
                              <div className="flex justify-between gap-2">
                                <span
                                  className="font-semibold"
                                  style={{ color: RD.accent }}
                                >
                                  {r.request_no}
                                </span>
                                <span className="text-gray-400">
                                  {r.role}
                                  {r.entity_stage ? ` · ${r.entity_stage}` : ""}
                                </span>
                              </div>
                              <div className="text-gray-600">{r.title}</div>
                              <div className="text-gray-400">
                                {r.entity_type}
                                {r.entity_ref ? ` ${r.entity_ref}` : ""}
                                {r.reason_for_error
                                  ? ` · ${r.reason_for_error}`
                                  : ""}
                                {r.reason_detail ? ` — ${r.reason_detail}` : ""}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* by reason + trend */}
        <div className="space-y-3">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-sm mb-3">By reason</h3>
            {byReason.length === 0 && (
              <div className="text-xs text-gray-400">No data</div>
            )}
            {byReason.map((r: any) => (
              <Bar
                key={r.reason}
                label={r.reason}
                value={r.count}
                max={maxReason}
              />
            ))}
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-sm mb-3">
              Monthly trend (confirmed errors)
            </h3>
            {trend.length === 0 && (
              <div className="text-xs text-gray-400">No data</div>
            )}
            {trend.map((t: any) => (
              <Bar
                key={t.month}
                label={t.month}
                value={t.count}
                max={maxTrend}
                color={RD.accentStrong}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDeskFaultDashboard;
